/**
 * AudienceBuilder (MKT-REDESIGN-01 M2) — build an audience from Pegasus facts
 * in plain words, watch the count change as you go, save it (dynamic or a
 * frozen snapshot). Every control writes one key of the AudienceSpec; the
 * database does the matching (pegasus_audience_count / _members).
 */
import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, RefreshCw, Users, X } from "lucide-react";
import { SlideOver } from "@/components/ui/SlideOver";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { displayCustomerName } from "@/lib/format";
import { useCan } from "@/lib/permissions/PermissionProvider";
import {
  cleanSpec, describeSpec, resolveAudience, useAudienceCount, useRefreshFacts, useSaveAudience,
  EQUIPMENT_LABEL, type AudienceMember, type AudienceSpec, type EquipmentKind, type Refrigerant, type SavedAudience,
} from "./audiences";

const inp = "h-9 rounded-lg border border-hairline bg-white px-2.5 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30";
const YEAR = new Date().getFullYear();

export function AudienceBuilder({ initial, onClose, onSaved }: {
  initial?: { id?: string; name?: string; spec?: AudienceSpec; mode?: "dynamic" | "static" } | null;
  onClose: () => void;
  onSaved?: (a: SavedAudience) => void;
}) {
  const [spec, setSpec] = useState<AudienceSpec>(initial?.spec ?? {});
  const [name, setName] = useState(initial?.name ?? "");
  const [mode, setMode] = useState<"dynamic" | "static">(initial?.mode ?? "dynamic");
  const [debounced, setDebounced] = useState<AudienceSpec>(cleanSpec(spec));
  const [preview, setPreview] = useState<AudienceMember[] | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const count = useAudienceCount(debounced);
  const save = useSaveAudience();
  const refresh = useRefreshFacts();
  const canSend = useCan("marketing.send");
  useEffect(() => {
    const id = window.setTimeout(() => { setDebounced(cleanSpec(spec)); setPreview(null); }, 400);
    return () => window.clearTimeout(id);
  }, [spec]);
  const set = <K extends keyof AudienceSpec>(k: K, v: AudienceSpec[K]) => setSpec((s) => ({ ...s, [k]: v }));
  const summary = useMemo(() => describeSpec(cleanSpec(spec)), [spec]);
  const c = count.data;
  const factsAsOf = c?.facts_as_of ? new Date(c.facts_as_of).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null;

  const doPreview = async () => {
    setPreviewing(true);
    try { const { members } = await resolveAudience(debounced); setPreview(members.slice(0, 40)); }
    finally { setPreviewing(false); }
  };
  const doSave = () => {
    save.mutate({ id: initial?.id, name: name.trim() || summary, spec: cleanSpec(spec), mode, count: c ?? null }, { onSuccess: (a) => { onSaved?.(a); onClose(); } });
  };

  return (
    <SlideOver open onClose={onClose} size="lg" title={initial?.id ? "Edit audience" : "New audience"} subtitle={summary}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-slate-500">
            {c ? <><strong className="text-slate-800">{c.sendable.toLocaleString()}</strong> reachable by email · {c.matched.toLocaleString()} match</> : "Counting…"}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-hairline px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
            <button type="button" disabled={save.isPending || !canSend} onClick={doSave} title={canSend ? undefined : "Saving audiences needs the marketing.send permission"}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50">
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {initial?.id ? "Save changes" : "Save audience"}
            </button>
          </div>
        </div>
      }>
      <div className="space-y-5">
        {/* Name + mode */}
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-slate-500">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={summary} className={cn(inp, "w-full")} />
          </label>
          <div>
            <span className="mb-1 block text-[11px] font-medium text-slate-500">Keeps up to date?</span>
            <div className="inline-flex overflow-hidden rounded-lg border border-hairline">
              {(["dynamic", "static"] as const).map((m) => (
                <button key={m} type="button" onClick={() => setMode(m)} className={cn("px-3 py-2 text-xs font-semibold", mode === m ? "bg-brand-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50")}>
                  {m === "dynamic" ? "Yes — re-check each time" : "No — freeze this list"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* The count */}
        <div className="rounded-xl border border-brand-100 bg-brand-50/40 p-3">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-2xl font-extrabold tabular-nums text-brand-800">{c ? c.sendable.toLocaleString() : count.isFetching ? "…" : "—"}</span>
            <span className="text-sm text-slate-700">customers we can email</span>
            {c && (
              <span className="text-xs text-slate-500">
                of {c.matched.toLocaleString()} matching{c.no_endpoint ? ` · ${c.no_endpoint.toLocaleString()} no email` : ""}{c.unsubscribed ? ` · ${c.unsubscribed.toLocaleString()} unsubscribed` : ""}
              </span>
            )}
            {count.isFetching && <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-500" />}
          </div>
          {c?.sample?.length ? <div className="mt-1 text-[11.5px] text-slate-500">e.g. {c.sample.map((n) => displayCustomerName(null, n)).join(", ")}…</div> : null}
          <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
            {factsAsOf && <span>Facts as of {factsAsOf}</span>}
            {canSend && (
              <button type="button" disabled={refresh.isPending} onClick={() => refresh.mutate()} className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline disabled:opacity-50">
                <RefreshCw className={cn("h-3 w-3", refresh.isPending && "animate-spin")} /> {refresh.isPending ? "Rebuilding facts…" : "Refresh facts now"}
              </button>
            )}
            <button type="button" disabled={previewing || !c} onClick={doPreview} className="inline-flex items-center gap-1 font-medium text-slate-600 hover:underline disabled:opacity-50">
              <Users className="h-3 w-3" /> {previewing ? "Loading…" : "Preview the list"}
            </button>
          </div>
          {preview && (
            <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-hairline bg-white">
              <table className="w-full text-xs">
                <tbody>
                  {preview.map((m) => (
                    <tr key={m.custno} className="border-b border-slate-50">
                      <td className="px-2 py-1 font-medium text-slate-700">{displayCustomerName(null, m.name) || m.custno}</td>
                      <td className="px-2 py-1 text-slate-500">{m.city}</td>
                      <td className="px-2 py-1 text-slate-500">{m.email ?? <span className="text-slate-300">no email</span>}</td>
                      <td className="px-2 py-1 text-right">{m.sendable ? <span className="text-emerald-700">ok</span> : <span className="text-slate-400">{m.exclude_reason?.replace("_", " ")}</span>}</td>
                    </tr>
                  ))}
                  {preview.length === 0 && <tr><td className="px-2 py-3 text-center text-slate-400">Nobody matches.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Plan */}
        <Group title="Maintenance plan">
          <Chips value={spec.plan ?? "any"} onChange={(v) => set("plan", v as AudienceSpec["plan"])}
            options={[["any", "Any"], ["member", "Members"], ["lapsed", "Lapsed"], ["none", "No plan"]]} />
          <NumberRow label="Plan expires within" unit="days" value={spec.plan_expires_within_days ?? null} onChange={(v) => set("plan_expires_within_days", v)} hint="members only" />
          <NumberRow label="Plan ended within" unit="days" value={spec.plan_expired_within_days ?? null} onChange={(v) => set("plan_expired_within_days", v)} hint="lapsed only" />
        </Group>

        {/* Service history */}
        <Group title="Service history">
          <NumberRow label="No visit in the last" unit="days" value={spec.last_service_over_days ?? null} onChange={(v) => set("last_service_over_days", v)} presets={[[365, "1 yr"], [540, "18 mo"], [730, "2 yrs"]]} />
          {spec.last_service_over_days ? (
            <label className="ml-1 inline-flex items-center gap-1.5 text-xs text-slate-600">
              <input type="checkbox" checked={!!spec.include_never_served} onChange={(e) => set("include_never_served", e.target.checked)} className="h-3.5 w-3.5 accent-brand-700" /> include customers we've never visited
            </label>
          ) : null}
          <NumberRow label="Visited in the last" unit="days" value={spec.last_service_within_days ?? null} onChange={(v) => set("last_service_within_days", v)} presets={[[90, "90 d"], [365, "1 yr"]]} />
        </Group>

        {/* Equipment */}
        <Group title="Equipment on file">
          <div className="flex flex-wrap gap-1.5">
            {(Object.keys(EQUIPMENT_LABEL) as EquipmentKind[]).map((k) => {
              const on = (spec.equipment ?? []).includes(k);
              return (
                <button key={k} type="button" onClick={() => set("equipment", on ? (spec.equipment ?? []).filter((x) => x !== k) : [...(spec.equipment ?? []), k])}
                  className={cn("rounded-full border px-2.5 py-1 text-xs font-medium", on ? "border-brand-300 bg-brand-50 text-brand-800" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")}>
                  {EQUIPMENT_LABEL[k]}
                </button>
              );
            })}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <YearRow label="A/C installed before" value={spec.ac_installed_before ?? null} onChange={(v) => set("ac_installed_before", v)} />
            <YearRow label="Furnace installed before" value={spec.furnace_installed_before ?? null} onChange={(v) => set("furnace_installed_before", v)} />
          </div>
          <NumberRow label="Any system older than" unit="years" value={spec.system_older_than_years ?? null} onChange={(v) => set("system_older_than_years", v)} presets={[[10, "10"], [15, "15"], [20, "20"]]} />
          <div>
            <span className="mr-2 inline-flex items-center gap-1.5 text-xs text-slate-600">
              Likely refrigerant
              <InfoTip label="How the refrigerant is guessed">From the install year and model code: R-22 before 2010, R-454B for 2025 and later or Lennox K models, R-410A otherwise.</InfoTip>
            </span>
            <Chips value={spec.refrigerant ?? ""} onChange={(v) => set("refrigerant", (v || null) as Refrigerant | null)}
              options={[["", "Any"], ["R-22", "R-22 (pre-2010)"], ["R-410A", "R-410A"], ["R-454B", "R-454B (new)"]]} />
          </div>
        </Group>

        {/* Location */}
        <Group title="Location">
          <div className="grid gap-2 sm:grid-cols-3">
            <ListRow label="Cities" placeholder="Eagan, Apple Valley" value={spec.city ?? []} onChange={(v) => set("city", v)} />
            <ListRow label="ZIP codes" placeholder="55121, 55124" value={spec.zip ?? []} onChange={(v) => set("zip", v)} />
            <ListRow label="Zones" placeholder="South, West" value={spec.zone ?? []} onChange={(v) => set("zone", v)} />
          </div>
        </Group>

        {/* Contact & value */}
        <Group title="Contact & value">
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={!!spec.has_email} onChange={(e) => set("has_email", e.target.checked)} className="h-3.5 w-3.5 accent-brand-700" /> Has an email</label>
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={!!spec.has_mobile} onChange={(e) => set("has_mobile", e.target.checked)} className="h-3.5 w-3.5 accent-brand-700" /> Has a cell number</label>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <MoneyRow label="Lifetime revenue at least" value={spec.ltv_min_cents ?? null} onChange={(v) => set("ltv_min_cents", v)} />
            <MoneyRow label="Lifetime revenue under" value={spec.ltv_max_cents ?? null} onChange={(v) => set("ltv_max_cents", v)} />
          </div>
          <ListRow label="Lead source codes" placeholder="REF, INTER" value={spec.lead_source ?? []} onChange={(v) => set("lead_source", v)} />
        </Group>

        {/* Exclusions */}
        <Group title={<>Leave out<InfoTip className="normal-case" label="Who is always left out">Unsubscribed and no-email customers are always left out of the send. They still show in the count above, so nothing is hidden.</InfoTip></>}>
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={!!spec.exclude_balance} onChange={(e) => set("exclude_balance", e.target.checked)} className="h-3.5 w-3.5 accent-brand-700" /> Customers who owe a balance</label>
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" checked={spec.active_only !== false} onChange={(e) => set("active_only", e.target.checked ? true : false)} className="h-3.5 w-3.5 accent-brand-700" /> Inactive accounts</label>
          </div>
        </Group>
      </div>
    </SlideOver>
  );
}

function Group({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-hairline bg-white p-3">
      <h4 className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{title}</h4>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
function Chips({ value, options, onChange }: { value: string; options: [string, string][]; onChange: (v: string) => void }) {
  return (
    <div className="inline-flex flex-wrap gap-1">
      {options.map(([v, label]) => (
        <button key={v} type="button" onClick={() => onChange(v)} className={cn("rounded-full border px-2.5 py-1 text-xs font-medium", value === v ? "border-brand-300 bg-brand-50 text-brand-800" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")}>{label}</button>
      ))}
    </div>
  );
}
function NumberRow({ label, unit, value, onChange, hint, presets }: { label: string; unit: string; value: number | null; onChange: (v: number | null) => void; hint?: string; presets?: [number, string][] }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
      <span className="min-w-[150px]">{label}</span>
      <input type="number" min={0} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className={cn(inp, "w-24 text-right")} />
      <span>{unit}</span>
      {presets?.map(([n, l]) => <button key={n} type="button" onClick={() => onChange(n)} className="rounded-full border border-hairline bg-white px-2 py-0.5 text-[11px] font-medium text-slate-500 hover:bg-slate-50">{l}</button>)}
      {value != null && <button type="button" onClick={() => onChange(null)} className="text-slate-400 hover:text-slate-700" aria-label="Clear"><X className="h-3.5 w-3.5" /></button>}
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </div>
  );
}
function YearRow({ label, value, onChange }: { label: string; value: number | null; onChange: (v: number | null) => void }) {
  return (
    <label className="flex items-center gap-2 text-xs text-slate-600">
      <span className="min-w-[150px]">{label}</span>
      <input type="number" min={1950} max={YEAR + 1} value={value ?? ""} placeholder="year" onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className={cn(inp, "w-24 text-right")} />
    </label>
  );
}
function MoneyRow({ label, value, onChange }: { label: string; value: number | null; onChange: (v: number | null) => void }) {
  return (
    <label className="flex items-center gap-2 text-xs text-slate-600">
      <span className="min-w-[150px]">{label}</span>
      <span className="text-slate-400">$</span>
      <input type="number" min={0} step={100} value={value != null ? Math.round(value / 100) : ""} onChange={(e) => onChange(e.target.value === "" ? null : Math.round(Number(e.target.value) * 100))} className={cn(inp, "w-28 text-right")} />
    </label>
  );
}
function ListRow({ label, placeholder, value, onChange }: { label: string; placeholder: string; value: string[]; onChange: (v: string[]) => void }) {
  const [text, setText] = useState(value.join(", "));
  useEffect(() => { setText(value.join(", ")); }, [value]);
  return (
    <label className="block text-xs text-slate-600">
      <span className="mb-1 block">{label}</span>
      <input value={text} onChange={(e) => setText(e.target.value)} onBlur={() => onChange(text.split(/[,\n]/).map((s) => s.trim()).filter(Boolean))}
        placeholder={placeholder} className={cn(inp, "w-full")} />
    </label>
  );
}
